import React, { useMemo, useState } from 'react';
import { EditProfilType, landnrToNumber } from '../practiceplaceprofileutils';
import { PraksisstedInfoPutDto as UHPraksisstedInfoPutDto } from '../../../api/generated/studiested-generated-api';
import { PraksisstedInfoPutDto as PSPraksisstedInfoPutDto } from '../../../api/generated/praksissted-generated-api';
import { useIntl } from 'react-intl';
import translations from '../practiceplaceprofile.i18n';
import validatePhoneNo from '../../../util/validatePhoneNo';
import validateEmail from '../../../util/validateEmail';
import { validatePostnr } from '../../../util/validatePostnr';
import { ConfirmationDialog } from '../../../components/dialog';
import * as css from '../practiceplaceprofile.module.scss';
import { Grid } from '@mui/material';
import { FakeLockedInput } from '../../../components/input';
import { TextInput } from '../../../components/input/textInput';
import { NumberInput } from '../../../components/input/numberInput';

type PraksisstedInfoPut = UHPraksisstedInfoPutDto | PSPraksisstedInfoPutDto;

export const EditProfileModal: React.FC<
  React.PropsWithChildren<{
    data: EditProfilType;
    handleSaveContactInfo: (updatePraksissted: PraksisstedInfoPut) => void;
    handleClose: () => void;
    isOpen: boolean;
  }>
> = ({ data, handleSaveContactInfo, handleClose, isOpen }) => {
  const { formatMessage } = useIntl();

  const labels = {
    title: formatMessage({ id: translations.endreInfoTitle.id }),
    subtitle: formatMessage({ id: translations.endreInfoSubtitle.id }),
    submit: formatMessage({ id: translations.submit.id }),
    orgnavn: formatMessage({ id: translations.organisasjon.id }),
    orgnr: formatMessage({ id: translations.orgnr.id }),
    adresselinje1: formatMessage({ id: translations.adresselinje1.id }),
    adresselinje2: formatMessage({ id: translations.adresselinje2.id }),
    postnr: formatMessage({ id: translations.postnr.id }),
    poststed: formatMessage({ id: translations.poststed.id }),
    epost: formatMessage({ id: translations.epost.id }),
    telefon: formatMessage({ id: translations.telefon.id }),
    landnr: formatMessage({ id: translations.landnr.id }),
  };

  const { adresse } = data;
  const [epost, setEpost] = useState(data.epost);
  const [adresselinje1, setAdresselinje1] = useState(adresse.adresselinje1);
  const [adresselinje2, setAdresselinje2] = useState(adresse.adresselinje2);
  const [telefonlandnr, setTelefonlandnr] = useState(
    landnrToNumber(data.telefonlandnr),
  );
  const [telefon, setTelefon] = useState(data.telefonnummer);
  const [postnr, setPostnr] = useState(adresse.postnr);
  const [poststed, setPoststed] = useState(adresse.poststed);

  const telefonHasChanged = useMemo(
    () =>
      telefon !== data.telefonnummer ||
      telefonlandnr !== landnrToNumber(data.telefonlandnr),
    [telefon, telefonlandnr, data.telefonlandnr, data.telefonnummer],
  );

  const adresseHasChanged = useMemo(() => {
    const adr1Changed = adresse.adresselinje1 !== adresselinje1;
    const adr2Changed = adresse.adresselinje2 !== adresselinje2;
    const poststedChanged = adresse.poststed !== poststed;
    const postnrChanged = adresse.postnr !== postnr;

    return adr1Changed || adr2Changed || poststedChanged || postnrChanged;
  }, [adresse, adresselinje1, adresselinje2, poststed, postnr]);

  const epostHasChanged = useMemo(
    () => epost !== data.epost,
    [epost, data.epost],
  );

  // Should not be allowed to save changes without adresselinje1, poststed, postnr
  const validFormData = useMemo(() => {
    const validTelefonData =
      (!telefon && !telefonlandnr) ||
      (telefonlandnr && telefon && validatePhoneNo(telefonlandnr + telefon));

    const validEpostData = !epost || validateEmail(epost);
    const validAdresseData =
      adresselinje1 && poststed && postnr && validatePostnr(postnr);

    return validTelefonData && validEpostData && validAdresseData;
  }, [epost, telefon, telefonlandnr, adresselinje1, poststed, postnr]);

  const submitDisabled = useMemo(
    () =>
      !(
        validFormData &&
        (adresseHasChanged || epostHasChanged || telefonHasChanged)
      ),
    [validFormData, adresseHasChanged, epostHasChanged, telefonHasChanged],
  );

  const saveContactInfoEdit = (save: boolean) => {
    save &&
      validFormData &&
      handleSaveContactInfo({
        epost: epost || undefined,
        telefonlandnr: telefon ? telefonlandnr?.toString() : undefined,
        telefonnummer: telefonlandnr ? telefon : undefined,
        adresselinje1: adresselinje1,
        adresselinje2: adresselinje2 || undefined,
        postnr: postnr,
        poststed: poststed,
      });
    handleClose();

    if (!save) {
      // TODO Replace useState with useReducer to have a one line state reset
      setEpost(data.epost);
      setAdresselinje1(adresse.adresselinje1);
      setAdresselinje2(adresse.adresselinje2);
      setTelefon(data.telefonnummer);
      setTelefonlandnr(landnrToNumber(data.telefonlandnr));
      setPostnr(adresse.postnr);
      setPoststed(adresse.poststed);
    }
  };

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      title={labels.title}
      submitLabel={labels.submit}
      subtitle={labels.subtitle}
      extraClasses={[css.editProfileModal]}
      onClose={(ok) => saveContactInfoEdit(ok)}
      submitDisabled={submitDisabled}
    >
      <Grid md={12} lg={12} item>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FakeLockedInput label={labels.orgnavn} value={data.navn} />
          </Grid>
          <Grid item xs={6}>
            <FakeLockedInput label={labels.orgnr} value={data.orgnr} />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextInput
              htmlId={'adresselinje1'}
              label={labels.adresselinje1}
              value={adresselinje1}
              autoComplete="off"
              onChange={setAdresselinje1}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <TextInput
              htmlId={'adresselinje2'}
              label={labels.adresselinje2}
              value={adresselinje2}
              autoComplete="off"
              onChange={setAdresselinje2}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={2}>
            <TextInput
              htmlId={'postnr'}
              label={labels.postnr}
              value={postnr}
              autoComplete="off"
              onChange={setPostnr}
              required
            />
          </Grid>
          <Grid item xs={4}>
            <TextInput
              htmlId={'poststed'}
              label={labels.poststed}
              value={poststed}
              autoComplete="off"
              onChange={setPoststed}
              required
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextInput
              htmlId={'epost'}
              label={labels.epost}
              value={epost}
              autoComplete="off"
              onChange={setEpost}
              type="email"
            />
          </Grid>
          <Grid item xs={2}>
            <NumberInput
              htmlId={'telefonlandnr'}
              label={labels.landnr}
              value={telefonlandnr}
              onChange={(value) => setTelefonlandnr(value)}
            />
          </Grid>
          <Grid item xs={4}>
            <TextInput
              htmlId={'telefon'}
              label={labels.telefon}
              value={telefon}
              autoComplete="off"
              onChange={setTelefon}
              type="tel"
            />
          </Grid>
        </Grid>
      </Grid>
    </ConfirmationDialog>
  );
};
